import { Icon, Icons } from '@/components/icon';
import { useTranslation } from '@/hooks/use-translation';
import { cn } from '@/lib/utils';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Combobox, ComboboxGroup, ComboboxItem } from '../ui/combobox';
import { P3 } from '../ui/text';
import { type ColumnOption, FilterButton } from './filter-button';
import {
  type ColumnFilterConfig,
  FilterChip,
  type FilterCondition,
  type FilterValue,
} from './filter-chip';

// Re-export for types.ts
export type { ColumnOption as FilterableColumn } from './filter-button';

export interface FilterEvent {
  type: 'applied' | 'removed';
  filter: FilterValue;
  activeFilters: FilterValue[];
}

interface FilterBarProps {
  columns: ColumnOption[];
  onFiltersChange?: (filters: FilterValue[]) => void;
  className?: string;
  /** Initial filters to populate the filter bar */
  initialFilters?: FilterValue[];
  /** Content to render at the beginning of the filter bar (e.g., sort button) */
  leadingContent?: React.ReactNode;
  /** Content to render at the end of the filter bar (e.g., save view button) */
  trailingContent?: React.ReactNode;
  /** Custom filter configurations per column (keyed by column value/id) */
  columnConfigs?: Record<string, ColumnFilterConfig>;
  /** Callback fired when a filter is applied or removed, for analytics tracking */
  onFilterEvent?: (event: FilterEvent) => void;
}

// Conditions that don't require a value input
const conditionsWithoutValue: FilterCondition[] = ['empty', 'not_empty'];

// Generate unique filter ID
let filterIdCounter = 0;
function generateFilterId(): string {
  return `filter-${Date.now()}-${++filterIdCounter}`;
}

// Ensure filters have IDs
function ensureFilterIds(filters: FilterValue[]): FilterValue[] {
  return filters.map((f) => (f.id ? f : { ...f, id: generateFilterId() }));
}

export function FilterBar({
  columns,
  onFiltersChange,
  className,
  initialFilters,
  leadingContent,
  trailingContent,
  columnConfigs,
  onFilterEvent,
}: FilterBarProps) {
  const { t } = useTranslation('desktop_ui');
  const [filters, setFilters] = useState<FilterValue[]>(() =>
    ensureFilterIds(initialFilters ?? []),
  );
  const initialFiltersRef = useRef(initialFilters);
  const searchAttributesText = t('components.contacts.filter.searchAttributes');
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  // Check scroll position to show/hide fade gradients
  const updateScrollState = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) {
      return;
    }

    const { scrollLeft, scrollWidth, clientWidth } = container;
    setCanScrollLeft(scrollLeft > 0);
    setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 1);
  }, []);

  // Update scroll state on scroll and on mount/resize
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) {
      return;
    }

    updateScrollState();
    container.addEventListener('scroll', updateScrollState);

    const resizeObserver = new ResizeObserver(updateScrollState);
    resizeObserver.observe(container);

    return () => {
      container.removeEventListener('scroll', updateScrollState);
      resizeObserver.disconnect();
    };
  }, [updateScrollState]);

  // Re-check scroll state after content changes (filters or leadingContent)
  useEffect(() => {
    const timeoutId = setTimeout(updateScrollState, 50);
    return () => clearTimeout(timeoutId);
  }, [filters.length, leadingContent, updateScrollState]);

  // Sync with external initialFilters when they change (but not on every render)
  useEffect(() => {
    if (initialFilters && initialFilters !== initialFiltersRef.current) {
      initialFiltersRef.current = initialFilters;
      setFilters(ensureFilterIds(initialFilters));
    }
  }, [initialFilters]);

  // Only notify parent when filter is complete and committed (Enter pressed or no-value condition)
  const notifyFiltersChange = useCallback(
    (newFilters: FilterValue[]) => {
      onFiltersChange?.(newFilters);
    },
    [onFiltersChange],
  );

  const handleAddFilter = useCallback(
    (columnId: string) => {
      const column = columns.find((c) => c.value === columnId);
      if (!column) {
        return;
      }

      // Check if there's an incomplete filter (no condition selected)
      const incompleteFilterIndex = filters.findIndex((f) => f.condition === null);

      if (incompleteFilterIndex !== -1) {
        // Update the incomplete filter's column instead of creating a new one
        const newFilters = filters.map((filter, i) =>
          i === incompleteFilterIndex
            ? {
                ...filter,
                columnId,
                columnLabel: column.label,
                columnIcon: column.icon,
                condition: null,
                value: undefined,
              }
            : filter,
        );
        setFilters(newFilters);
      } else {
        // All filters are complete, create a new one
        const newFilter: FilterValue = {
          id: generateFilterId(),
          columnId,
          columnLabel: column.label,
          columnIcon: column.icon,
          condition: null,
        };
        setFilters([...filters, newFilter]);
      }
    },
    [columns, filters],
  );

  const handleConditionChange = useCallback(
    (index: number, condition: FilterCondition, serverField?: string) => {
      const newFilters = filters.map((filter, i) =>
        i === index ? { ...filter, condition, serverField, value: undefined } : filter,
      );
      setFilters(newFilters);

      // Notify if condition doesn't require a value (only for default conditions)
      if (conditionsWithoutValue.includes(condition)) {
        notifyFiltersChange(newFilters);
        const appliedFilter = newFilters[index];
        if (appliedFilter) {
          onFilterEvent?.({
            type: 'applied',
            filter: appliedFilter,
            activeFilters: newFilters.filter((f) => f.condition !== null),
          });
        }
      }
    },
    [filters, notifyFiltersChange, onFilterEvent],
  );

  // Called when input value changes (on blur) - saves to state without notifying parent
  const handleInputSync = useCallback((index: number, value: string) => {
    setFilters((prev) => prev.map((filter, i) => (i === index ? { ...filter, value } : filter)));
  }, []);

  // Called when user presses Enter on the filter input
  const handleValueChange = useCallback(
    (index: number, value: string) => {
      // First sync all current input values to ensure we have the latest state
      setFilters((prev) => {
        const newFilters = prev.map((filter, i) => (i === index ? { ...filter, value } : filter));
        // Notify parent with the updated filters
        notifyFiltersChange(newFilters);
        const appliedFilter = newFilters[index];
        if (appliedFilter) {
          onFilterEvent?.({
            type: 'applied',
            filter: appliedFilter,
            activeFilters: newFilters.filter((f) => f.condition !== null),
          });
        }
        return newFilters;
      });
    },
    [notifyFiltersChange, onFilterEvent],
  );

  const handleColumnChange = useCallback(
    (index: number, columnId: string) => {
      const column = columns.find((c) => c.value === columnId);
      if (!column) {
        return;
      }

      const newFilters = filters.map((filter, i) =>
        i === index
          ? {
              ...filter,
              columnId,
              columnLabel: column.label,
              columnIcon: column.icon,
              condition: null,
              value: undefined,
            }
          : filter,
      );
      setFilters(newFilters);
    },
    [columns, filters],
  );

  const handleRemoveFilter = useCallback(
    (index: number) => {
      const removedFilter = filters[index];
      const newFilters = filters.filter((_, i) => i !== index);
      setFilters(newFilters);
      // Defer parent notification to allow local state update to render first
      queueMicrotask(() => {
        notifyFiltersChange(newFilters);
      });
      if (removedFilter) {
        onFilterEvent?.({
          type: 'removed',
          filter: removedFilter,
          activeFilters: newFilters.filter((f) => f.condition !== null),
        });
      }
    },
    [filters, notifyFiltersChange, onFilterEvent],
  );

  // Allow duplicate filters on the same column - show all columns as available
  const availableColumns = columns;

  const addFilterButton = (
    <button
      type="button"
      className="h-7 shrink-0 px-2 flex cursor-pointer items-center justify-center rounded-full border-dashed border border-primary-10 bg-primary-3 outline-none hover:bg-primary-5 hover:border-primary-20"
    >
      <Icon name={Icons.Plus} className="text-s text-primary-70" />
    </button>
  );

  return (
    <div className={cn('h-12 flex items-center gap-2 px-3 py-2', className)}>
      {/* Leading content (sort button) - sticky */}
      {leadingContent && <div className="shrink-0">{leadingContent}</div>}

      {/* Scrollable filters list + add button */}
      {filters.length > 0 ? (
        <div
          ref={scrollContainerRef}
          className={cn(
            'flex h-full flex-1 min-w-0 items-center gap-2 overflow-x-auto scrollbar-hide',
            canScrollLeft &&
              canScrollRight &&
              '[mask-image:linear-gradient(to_right,transparent_0%,black_16px,black_calc(100%_-_16px),transparent_100%)]',
            canScrollLeft &&
              !canScrollRight &&
              '[mask-image:linear-gradient(to_right,transparent_0%,black_16px,black_100%)]',
            !canScrollLeft &&
              canScrollRight &&
              '[mask-image:linear-gradient(to_right,black_0%,black_calc(100%_-_16px),transparent_100%)]',
          )}
        >
          {filters.map((filter, index) => (
            <FilterChip
              key={filter.id}
              filter={filter}
              availableColumns={availableColumns}
              onColumnChange={(columnId) => handleColumnChange(index, columnId)}
              onConditionChange={(condition, serverField) =>
                handleConditionChange(index, condition, serverField)
              }
              onValueChange={(value) => handleValueChange(index, value)}
              onInputSync={(value) => handleInputSync(index, value)}
              onRemove={() => handleRemoveFilter(index)}
              columnConfig={columnConfigs?.[filter.columnId]}
            />
          ))}
          {availableColumns.length > 0 && (
            <div className="shrink-0">
              <Combobox
                variant="filter"
                searchPlaceholder={searchAttributesText}
                options={availableColumns}
                onValueChange={handleAddFilter}
                trigger={addFilterButton}
              >
                {({ filteredOptions }) => (
                  <ComboboxGroup variant="filter">
                    {filteredOptions.map((option) => {
                      const columnOption = option as ColumnOption;
                      return (
                        <ComboboxItem key={option.value} option={option} variant="filter">
                          {(opt, _isSelected) => (
                            <div className="flex items-center gap-1">
                              {columnOption.icon && (
                                <Icon
                                  name={Icons[columnOption.icon]}
                                  className="text-s text-primary-80"
                                />
                              )}
                              <P3 className="text-primary-100">{opt.label}</P3>
                            </div>
                          )}
                        </ComboboxItem>
                      );
                    })}
                  </ComboboxGroup>
                )}
              </Combobox>
            </div>
          )}
        </div>
      ) : (
        <FilterButton columns={availableColumns} onColumnSelect={handleAddFilter} />
      )}

      {/* Spacer - only needed when no filters (scroll container has flex-1) */}
      {filters.length === 0 && <div className="flex-1" />}

      {/* Trailing content (save button) - always at the end */}
      {trailingContent && <div className="shrink-0">{trailingContent}</div>}
    </div>
  );
}

export type { FilterValue, FilterCondition, ColumnFilterConfig };
